import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Bell, AlertTriangle, Crown, Eye, ShieldCheck, Clock, Trash2, CheckCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const ICONS = {
  trial_ending: Crown,
  trial_expired: Crown,
  subscription_ending: Crown,
  subscription_expired: Crown,
  warning_issued: AlertTriangle,
  warning_cleared: ShieldCheck,
  case_status_changed: Clock,
  new_sighting: Eye,
};

const TYPE_COLOR = {
  trial_ending: 'text-amber-600 bg-amber-50',
  trial_expired: 'text-red-600 bg-red-50',
  subscription_ending: 'text-amber-600 bg-amber-50',
  subscription_expired: 'text-red-600 bg-red-50',
  warning_issued: 'text-red-600 bg-red-50',
  warning_cleared: 'text-emerald-600 bg-emerald-50',
  case_status_changed: 'text-blue-600 bg-blue-50',
  new_sighting: 'text-purple-600 bg-purple-50',
};

const POLL_MS = 60000;
const LAST_SEEN_KEY = 'misyou.notif.lastSeen';

async function showBrowserNotification(n) {
  try {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    if (!reg) return;
    await reg.showNotification(n.title || 'MISYOU', {
      body: n.message || '',
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      tag: n.notification_id,
      data: { url: n.link || '/dashboard' },
      requireInteraction: n.priority === 'high',
    });
  } catch (e) {
    // Silent — never break the UI for a missing notification permission
    console.warn('Browser notification skipped:', e);
  }
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationBell() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastSeenRef = useRef(localStorage.getItem(LAST_SEEN_KEY) || '');

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Request browser-notification permission once, soft-prompt on first bell open
  const requestPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (e) {
        // Some browsers throw if called outside a user gesture — non-fatal.
        console.warn('[notifications] Notification.requestPermission failed:', e?.message || e);
      }
    }
  }, []);

  const pollLatest = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/notifications`, {
        withCredentials: true,
        headers: authHeaders,
      });
      const data = r.data || {};
      const list = data.items || [];
      setUnread(data.unread_count || 0);
      // Fire browser notifications for items newer than lastSeen
      const lastSeen = lastSeenRef.current;
      const newOnes = list.filter((n) => !n.read && (!lastSeen || n.created_at > lastSeen));
      if (newOnes.length > 0) {
        // Deliver newest-first up to a sensible cap (3) to avoid spamming
        for (const n of newOnes.slice(0, 3)) {
          await showBrowserNotification(n);
        }
        const newest = list.reduce((acc, n) => (n.created_at > acc ? n.created_at : acc), lastSeen || '');
        if (newest && newest !== lastSeen) {
          lastSeenRef.current = newest;
          localStorage.setItem(LAST_SEEN_KEY, newest);
        }
      }
    } catch (e) {
      // Polling silently retries on the next tick — but log so we can
      // spot auth/network issues in dev tools without spamming the user.
      console.warn('[notifications] pollLatest failed:', e?.response?.status, e?.message);
    }
  }, [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications`, {
        withCredentials: true,
        headers: authHeaders,
      });
      const list = r.data?.items || [];
      setItems(list);
      setUnread(r.data?.unread_count || 0);
      // Also bump lastSeen so we don't re-notify items the user just saw
      const newest = list[0]?.created_at;
      if (newest && newest !== lastSeenRef.current) {
        lastSeenRef.current = newest;
        localStorage.setItem(LAST_SEEN_KEY, newest);
      }
    } catch (e) {
      console.error('Failed to fetch notifications', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    pollLatest();
    const t = setInterval(pollLatest, POLL_MS);
    return () => clearInterval(t);
  }, [token, pollLatest]);

  useEffect(() => {
    if (open) {
      fetchAll();
      requestPermission();
    }
  }, [open, fetchAll, requestPermission]);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`, {}, {
        withCredentials: true,
        headers: authHeaders,
      });
      setItems((prev) => prev.map((i) => (i.notification_id === id ? { ...i, read: true } : i)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      console.error(e);
    }
  };

  const dismiss = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`, {
        withCredentials: true,
        headers: authHeaders,
      });
      setItems((prev) => prev.filter((i) => i.notification_id !== id));
      pollLatest();
    } catch (e) {
      console.error(e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        withCredentials: true,
        headers: authHeaders,
      });
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
      setUnread(0);
    } catch (e) {
      console.error(e);
    }
  };

  const handleClick = async (n) => {
    if (!n.read) await markRead(n.notification_id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  if (!token) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
          data-testid="notification-bell-btn"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-slate-700" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-unread-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] p-0 max-h-[70vh] overflow-hidden flex flex-col"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-700" />
            <span className="font-semibold text-slate-900">Notifications</span>
            {unread > 0 && (
              <Badge className="bg-red-100 text-red-700 ml-1">{unread} new</Badge>
            )}
          </div>
          {items.some((i) => !i.read) && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-auto py-1 text-slate-600"
              onClick={markAllRead}
              data-testid="notification-mark-all-read-btn"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Bell className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              You're all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                const tone = TYPE_COLOR[n.type] || 'text-slate-600 bg-slate-100';
                return (
                  <li
                    key={n.notification_id}
                    className={`p-3 flex gap-3 hover:bg-slate-50 cursor-pointer relative ${!n.read ? 'bg-blue-50/40' : ''}`}
                    onClick={() => handleClick(n)}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${tone}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-semibold text-slate-900 ${!n.read ? '' : 'opacity-80'}`}>
                          {n.title}
                        </p>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 shrink-0" />}
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismiss(n.notification_id);
                      }}
                      className="opacity-0 group-hover:opacity-100 hover:opacity-100 self-start p-1 rounded hover:bg-slate-200 transition-opacity"
                      aria-label="Dismiss"
                      data-testid={`notification-dismiss-${n.notification_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-slate-500" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
